import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { createFileName, useScreenshot } from 'use-react-screenshot';

import IconTabChart from 'src/presentations/components/atom/icons/TabChart.tsx';
import IconTabTable from 'src/presentations/components/atom/icons/TabTable.tsx';
import { ICardAnalyticItemProps } from 'src/presentations/components/molecules/card/cardAnalytic/types';
import { DownloadButton } from 'src/presentations/components/molecules/dataTable/component/DownloadButton.tsx';
import { ColumnSettingsControl, ColumnVisibilityProvider } from 'src/presentations/components/molecules/table/columnSettings';
import { LayoutSidebar } from 'src/presentations/components/organisms/sidebar/LayoutSidebar.tsx';
import { useTourContext } from 'src/presentations/guided-tours';
import NewCloseIcon from 'src/assets/images/icons/new-close.svg';
import NewFilterTickIcon from 'src/assets/images/icons/new-filter-tick.svg';
import { LandingTopWidget } from '../landing';
import { ChartFilterTabs } from './chart/ChartFilterTabs';
import { AnalyticsChart } from './chart/AnalyticsChart';
import { TableTab } from './table/useTable';
import { FilterTabs } from './table/FilterTabs.tsx';
import { DownloadMenuWithFilters } from './table/DownloadMenuWithFilters';
import { AnalyticsTable, getColumnConfigForTab } from './table/AnalyticsTable';
import { AnalyticsHeader } from './filter/AnalyticsHeader';
import { useAnalyticsData } from './context/AnalyticsDataContext';
import { CardId } from './generalOverview/types';
import { ChartViewType } from './sidebar/types';
import { MetricsOverview } from './generalOverview/MetricsOverview';
import { filterChartGranularityMapper } from './analyticsMapper';

interface AnalyticsContentProps {
  hashedFilter: any;
  setHashedFilter: (filter: any) => void;
  globalCurrency?: string;
  selectedChartView: ChartViewType;
  setSelectedChartView: (view: ChartViewType) => void;
}

export const AnalyticsContent: FC<AnalyticsContentProps> = ({ hashedFilter, setHashedFilter, globalCurrency, selectedChartView, setSelectedChartView }) => {
  const { appTour, refreshTourSteps } = useTourContext();
  const tourChart = appTour.registerTour('reports-analytics-chart');
  const tourTable = appTour.registerTour('reports-analytics-table');
  // Get data from context
  const {
    keyMetrics,
    activeTab,
    setActiveTab,
    isKeyMetricsLoading,
    isChartLoading,
    isChartError,
    baseSearchRequest,
    activeFilterCount,
    clearAllFilters,
    filterMetadata,
    totals,
  } = useAnalyticsData();

  // Get form context
  const { setValue, watch } = useFormContext();
  const { name, ...formValues } = watch();

  // Local UI state
  const [tableTab, setTableTab] = useState<TableTab>(TableTab.SHIPMENTS);
  const [visitedTableTabs, setVisitedTableTabs] = useState<Set<TableTab>>(new Set([TableTab.SHIPMENTS]));
  const [moreActionEl, setMoreActionEl] = useState<HTMLButtonElement | null>(null);
  const [selectedCards, setSelectedCards] = useState<ICardAnalyticItemProps[]>(
    [CardId.NO_SHIPMENTS, CardId.TOTAL_AMOUNT, CardId.CHARGABLE_WEIGHT].map((defaultItemId) => keyMetrics.find((item) => item.id === defaultItemId))
  );

  const onClickMoreAction = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMoreActionEl(event.currentTarget);
  };

  const onCloseMoreAction = () => {
    setMoreActionEl(null);
  };

  const handleTableTabChange = useCallback((newValue: string) => {
    const newTab = newValue as TableTab;
    setTableTab(newTab);
    // Add the new tab to visitedTableTabs to enable API query for that tab
    setVisitedTableTabs((prev) => new Set(prev).add(newTab));
  }, []);

  // Clear all filters
  const handleClearFilters = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    clearAllFilters();
  };

  // Handle keyboard events for accessibility
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClearFilters(e);
    }
  };

  const contentRef = useRef<HTMLDivElement>();
  const [, takeScreenShot] = useScreenshot({
    type: 'image/jpeg',
    quality: 1.0,
  });

  useEffect(() => {
    setSelectedCards((currVal) => currVal.map((item) => keyMetrics.find((v) => v.id === item.id)));
  }, [keyMetrics]);

  // Sync defaults when filterMetadata loads
  const defaultYear = useMemo(() => {
    const currentYear = new Date().getFullYear().toString();
    const availableYears = filterMetadata?.filters?.dateFilters?.years;

    if (availableYears && availableYears.length > 0) {
      if (availableYears.includes(currentYear)) {
        return currentYear;
      }
      const sortedYears = [...availableYears].sort((a, b) => Number.parseInt(b, 10) - Number.parseInt(a, 10));
      return sortedYears[0];
    }
    return currentYear;
  }, [filterMetadata]);

  useEffect(() => {
    const currentCurrency = hashedFilter.filters.formValues.currencyReference;
    const currentYear = hashedFilter.filters.formValues.filters?.dateFilters?.years?.[0];
    const availableYears = filterMetadata?.filters?.dateFilters?.years;

    // Check if we need to update currency or year
    // Update currency if not set, using global or fallback
    const needsCurrencyUpdate = !currentCurrency && globalCurrency;

    // Update year if: no year is set, OR current year is not in available years list (once data is loaded)
    const yearNeedsRefresh = availableYears && currentYear && !availableYears.includes(currentYear);
    const noYearSet = !currentYear;
    const needsYearUpdate = defaultYear && (noYearSet || yearNeedsRefresh);

    if (needsCurrencyUpdate || needsYearUpdate) {
      setHashedFilter({
        name: hashedFilter.name,
        description: hashedFilter.description,
        filters: {
          selectedChartView: hashedFilter.filters.selectedChartView,
          formValues: {
            ...hashedFilter.filters.formValues,
            ...(needsCurrencyUpdate ? { currencyReference: globalCurrency || 'USD' } : {}),
            ...(needsYearUpdate
              ? {
                  filters: {
                    ...hashedFilter.filters.formValues.filters,
                    dateFilters: {
                      ...hashedFilter.filters.formValues.filters?.dateFilters,
                      years: [defaultYear],
                    },
                  },
                }
              : {}),
          },
        },
      });
    }
  }, [defaultYear, globalCurrency, hashedFilter, setHashedFilter, filterMetadata]);

  // Auto-switch to most recent year with data when current year has no records
  useEffect(() => {
    if (isKeyMetricsLoading) return;

    // isKeyMetricsLoading becomes false as soon as invoice API resolves,
    // but totals.invoices is populated one render later via useEffect.
    // Wait until invoice totals are available before deciding to switch.
    if (totals.invoices === null) return;

    const currentYear = new Date().getFullYear().toString();
    const selectedYear = hashedFilter.filters.formValues.filters?.dateFilters?.years?.[0];

    // Only act when the current year is selected but has zero records
    if (selectedYear !== currentYear) return;
    if (totals.shipments > 0 || totals.invoices > 0 || totals.charges > 0) return;

    // Pick the most recent prior year from filterMetadata, or fall back to currentYear - 1
    const availableYears = filterMetadata?.filters?.dateFilters?.years ?? [];
    const fallbackYear =
      availableYears.filter((y) => y !== currentYear).sort((a, b) => Number.parseInt(b, 10) - Number.parseInt(a, 10))[0] ??
      String(Number.parseInt(currentYear, 10) - 1);

    setHashedFilter({
      ...hashedFilter,
      filters: {
        ...hashedFilter.filters,
        formValues: {
          ...hashedFilter.filters.formValues,
          filters: {
            ...hashedFilter.filters.formValues.filters,
            dateFilters: {
              ...hashedFilter.filters.formValues.filters?.dateFilters,
              years: [fallbackYear],
            },
          },
        },
      },
    });
  }, [isKeyMetricsLoading, totals, hashedFilter, setHashedFilter, filterMetadata]);

  // Recompute tour steps when tab switches so the table tour auto-detects its steps
  useEffect(() => {
    refreshTourSteps();
  }, [activeTab, refreshTourSteps]);

  useEffect(() => {
    switch (selectedChartView) {
      case ChartViewType.TREND:
        setValue('granularity', 'DAILY');
        break;
      case ChartViewType.TRADE_LANE:
        setValue('granularity', 'COUNTRY');
        break;
      case ChartViewType.SERVICE_PROVIDER:
        setValue('granularity', 'SERVICE_PROVIDER_CODE');
        break;
      default:
        setValue('granularity', 'DAILY');
    }
  }, [selectedChartView, setValue]);

  const handleChangeChartType = (value: ChartViewType) => {
    switch (value) {
      case ChartViewType.SERVICE_PROVIDER: {
        setSelectedChartView(value);
        break;
      }
      case ChartViewType.CORRELATIONS:
      case ChartViewType.TRADE_LANE:
      case ChartViewType.BILLING_REFERENCE:
      case ChartViewType.TREND:
      default: {
        setSelectedChartView(value);
      }
    }
  };

  const updateFiltersOnChartClick = useCallback(
    (payload: any) => {
      // Log the payload to help with debugging
      console.log('Chart click payload:', payload);

      let granularityToUse = formValues.granularity;

      // Determine what kind of data point was clicked based on payload properties
      if (payload.serviceProviderCode !== undefined || payload.serviceProviderName !== undefined) {
        // Handle service provider click
        granularityToUse = 'SERVICE_PROVIDER_CODE';
      } else if (payload.modality !== undefined) {
        // Handle modality click
        granularityToUse = 'MODALITY';
      } else if (payload.vehicleId !== undefined) {
        // Handle vehicle ID click
        granularityToUse = 'VEHICLE_ID';
      }

      // Map the granularity to a filter field path
      const filterField = filterChartGranularityMapper(granularityToUse);

      if (filterField) {
        // Use the filter field path with the form's setValue
        setValue(filterField as any, [payload?.aggregatedUnit]);
      }
    },
    [formValues.granularity, setValue]
  );

  const onScreenShot = () => {
    takeScreenShot(contentRef.current).then((image: string, imageProps: Record<string, string>) => {
      const extension = imageProps?.extension || 'jpg';
      const fileName = `preview-${name}`;
      const a = document.createElement('a');
      a.href = image;
      a.download = createFileName(extension, fileName);
      a.click();
    });
  };

  return (
    <div id='my-superset-container' className='w-full h-full relative'>
      <LandingTopWidget title='Reports & Analytics' />
      <LayoutSidebar>
        <div className='bg-neutral-10 h-full relative flex flex-col'>
          <AnalyticsHeader
            setDefaultFilters={setHashedFilter}
            selectedChartView={selectedChartView}
            onScreenShot={onScreenShot}
            sanitizedFormValues={baseSearchRequest}
            allowedCurrencies={filterMetadata?.allowedCurrencies}
            isLoading={isKeyMetricsLoading}
          />
          <div ref={contentRef} className='flex-1 pt-14 xl:pt-16 flex flex-col h-full overflow-auto'>
            <Box className='bg-neutral-10 flex-1 flex flex-col h-full' sx={{ width: '100%' }}>
              <div id={tourChart.registerStep('metricsPanel')}>
                <MetricsOverview selectedCards={selectedCards} setSelectedCards={setSelectedCards} />
              </div>

              <div className='flex-1 overflow-y-auto w-full pt-14 xl:pt-18 px-0'>
                <div className='flex  shadow-[0_10px_24px_0_rgba(0,0,0,0.1)] h-full'>
                  <ColumnVisibilityProvider
                    defaultColumns={getColumnConfigForTab(tableTab).defaultColumns}
                    storageKey={getColumnConfigForTab(tableTab).storageKey}
                  >
                    <div className='w-full flex-1 flex flex-col bg-white  border-neutral-20'>
                      {/* Tab Buttons */}
                      <div className='flex justify-between items-center gap-8 px-12 py-8 border-b border-t border-neutral-20'>
                        {/* Left Side */}
                        <div className='flex items-center gap-12'>
                          {/* Table Tabs - Only show when table tab is active */}
                          {activeTab === 'table' && <FilterTabs setTableTab={handleTableTabChange} tableTab={tableTab} />}
                          {activeTab === 'chart' && (
                            <ChartFilterTabs
                              selectedGranularity={formValues.granularity}
                              onGranularityChange={(granularity) => setValue('granularity', granularity)}
                            />
                          )}

                          {/* Download, Column Settings, and Filter Result - Only show in table view */}
                          {activeTab === 'table' && (
                            <>
                              <DownloadButton onClickMoreAction={onClickMoreAction} isOpen={!!moreActionEl} />
                              <DownloadMenuWithFilters tableTab={tableTab} moreActionEl={moreActionEl} onCloseMoreAction={onCloseMoreAction} />
                              <ColumnSettingsControl />

                              {/* Filter Counter Badge - After Columns Button */}
                              {activeFilterCount > 0 && (
                                <button
                                  type='button'
                                  onClick={handleClearFilters}
                                  className='flex items-center justify-center gap-8 py-7 px-10 rounded-[50px] cursor-pointer hover:bg-neutral-30 transition-colors border-0'
                                  style={{ backgroundColor: '#EDEEF2' }}
                                  title='Clear all filters'
                                >
                                  <img src={NewFilterTickIcon} alt='Filter' className='w-14 h-14' />
                                  <span className='text-12 font-medium' style={{ color: '#808080' }}>
                                    {activeFilterCount} {activeFilterCount === 1 ? 'Column' : 'Columns'} Filtered
                                  </span>
                                  <img src={NewCloseIcon} alt='Clear filters' className='w-14 h-14' />
                                </button>
                              )}
                            </>
                          )}
                        </div>

                        {/* Right Side - Chart/Table Switcher */}
                        <div
                          id={tourChart.registerStep('chartVisualizationTabs')}
                          className='flex items-center gap-4 p-6 bg-neutral-10 border border-neutral-20 rounded-12'
                        >
                          <button
                            type='button'
                            onClick={() => setActiveTab('chart')}
                            className={`flex items-center gap-6 px-10 py-6 rounded-8 transition-all ${
                              activeTab === 'chart' ? 'bg-primary' : 'hover:bg-neutral-20'
                            }`}
                            style={{ color: activeTab === 'chart' ? '#FFFFFF' : '#9F9F9F' }}
                            aria-label='Chart view'
                          >
                            <IconTabChart size={16} />
                            <span className="font-['Roboto'] text-12 font-normal">Chart</span>
                          </button>
                          <button
                            id={tourChart.registerStep('clickTableButton')}
                            type='button'
                            onClick={() => setActiveTab('table')}
                            className={`flex items-center gap-6 px-10 py-6 rounded-8 transition-all ${
                              activeTab === 'table' ? 'bg-primary' : 'hover:bg-neutral-20'
                            }`}
                            style={{ color: activeTab === 'table' ? '#FFFFFF' : '#9F9F9F' }}
                            aria-label='Table view'
                          >
                            <IconTabTable size={16} />
                            <span className="font-['Roboto'] text-12 font-normal">Table</span>
                          </button>
                        </div>
                      </div>

                      {/* Tab Content */}
                      {activeTab === 'chart' && (
                        <div className='flex-1 flex flex-col overflow-hidden'>
                          {(isChartLoading || isKeyMetricsLoading) && (
                            <div className='flex-1 flex justify-center items-center'>
                              <div className='flex flex-col items-center gap-16'>
                                <CircularProgress color='primary' />
                                <div className='text-14 text-neutral-60'>
                                  {isKeyMetricsLoading && 'Loading key metrics data...'}
                                  {!isKeyMetricsLoading && isChartLoading && 'Loading chart data...'}
                                </div>
                              </div>
                            </div>
                          )}
                          {!isChartLoading && !isKeyMetricsLoading && isChartError && (
                            <div className='flex-1 flex justify-center items-center'>
                              <div className='flex flex-col items-center gap-16 text-center'>
                                <div className='text-16 text-red-500 font-semibold'>Failed to load chart data</div>
                                <div className='text-14 text-neutral-60'>Please try refreshing the page or adjusting your filters</div>
                              </div>
                            </div>
                          )}
                          {!isChartLoading && !isKeyMetricsLoading && !isChartError && (
                            <div id={tourChart.registerStep('chartTableToggle')} className='flex-1 flex flex-col'>
                              <AnalyticsChart
                                selectedCards={selectedCards}
                                selectedChartView={selectedChartView}
                                formValues={formValues}
                                updateFiltersOnChartClick={updateFiltersOnChartClick}
                              />
                            </div>
                          )}
                        </div>
                      )}

                      {activeTab === 'table' && (
                        <div id={tourTable.registerStep('dataTables')} className='flex-1 flex flex-col'>
                          <AnalyticsTable tableTab={tableTab} visitedTabs={visitedTableTabs} hideFilterNav />
                        </div>
                      )}
                    </div>
                  </ColumnVisibilityProvider>
                </div>
              </div>
            </Box>
          </div>
        </div>
      </LayoutSidebar>
    </div>
  );
};
